'use client';

// @spec authentication-environment-switch authentication-reauth

import type { LinkedAccount } from '@client/services/family-config-service';
import { LINK_INITIATOR_USER_ID, LINK_INTENT_COOKIE } from '@fpo3/famfam-core/account-linking';
import { REAUTH_EXPECTED_COOKIE } from '@fpo3/famfam-core/constants';
import {
  computeGoogleScopes,
  GOOGLE_OAUTH_AUTHORIZATION_PARAMS,
} from '@fpo3/famfam-core/google-oauth-scopes';
import { REAUTH_STORAGE_KEYS, removeReauthStorageKey } from '@fpo3/famfam-core/reauth-storage-keys';
import { withBasePath } from '@fpo3/ts-commons-core/base-path';
import { setClientCookie } from '@fpo3/ts-commons-core/cookie';
import { safeSessionWrite } from '@fpo3/ts-commons-core/safe-session-storage';
import { signIn } from 'next-auth/react';

const AUTH_ORIGIN = (() => {
  const raw = process.env.NEXT_PUBLIC_AUTH_ORIGIN;
  if (raw == null) return undefined;
  try {
    return new URL(raw).origin;
  } catch {
    return undefined;
  }
})();

/**
 * Compute the joined scope string for the OAuth URL when extras were
 * requested. Returns `undefined` when no extras were passed so callers can
 * fall through to provider defaults instead of overriding with the base list.
 *
 * Centralizing this here keeps the cross-env and same-origin branches of
 * `initiateGoogleAuthRedirect` from drifting on the null/empty guard, and
 * gives a single place to apply any future normalization (e.g. trim).
 */
function scopeParamFor(extras: readonly string[] | undefined): string | undefined {
  if (extras == null) return undefined;
  // Defense-in-depth — `computeGoogleScopes` already drops blank/whitespace
  // entries, but pre-filtering keeps `scopeParamFor` honest about what it
  // does and avoids touching the composer with empty input.
  const normalizedExtras = extras.map((s) => s.trim()).filter((s) => s !== '');
  if (normalizedExtras.length === 0) return undefined;
  return computeGoogleScopes(normalizedExtras).join(' ');
}

/** Whether this environment delegates OAuth to a different origin. */
export function isCrossEnvironmentAuth(): boolean {
  return (
    AUTH_ORIGIN != null && globalThis.location != null && AUTH_ORIGIN !== globalThis.location.origin
  );
}

interface GoogleAuthRedirectOptions {
  /** The URL to return to after auth completes. */
  callbackUrl: string;
  /** Email hint for Google's login screen. */
  loginHint?: string | null;
  /** Current user ID, used to set the REAUTH_EXPECTED_COOKIE and expectedUserId param. */
  userId?: string | null;
  /**
   * Stored in sessionStorage as `famfam:google-reconnect-return` so the
   * reauth banner can redirect back after reconnection.
   */
  reconnectReturnPath: string;
  /**
   * Internal Account row id of the account being reconnected. When set, it is
   * persisted to sessionStorage so the post-reconnect cleanup DELETE can be
   * scoped to just that account. When null/undefined, any prior value is
   * removed and the post-reconnect DELETE falls back to clearing all accounts.
   * Callers that initiate a per-account reauth (the banner and
   * `startReauthFlow`) should always supply this; general sign-in / link
   * flows omit it.
   */
  accountId?: string | null;
  /**
   * Extra OAuth scopes to request on top of the base list (e.g.
   * `['gmail.readonly']` for the email-intelligence opt-in flow). Short
   * forms are expanded to googleapis URLs; the resulting full scope string
   * overrides the provider's default `scope` authorization param.
   */
  additionalScopes?: readonly string[];
}

/**
 * Initiates a Google OAuth redirect for re-authentication or initial sign-in.
 * Handles cross-environment auth (delegating to a different origin) and
 * direct NextAuth signIn.
 */
export async function initiateGoogleAuthRedirect(
  options: GoogleAuthRedirectOptions
): Promise<void> {
  const { callbackUrl, loginHint, userId, reconnectReturnPath, accountId, additionalScopes } =
    options;

  // Store expected user ID so the server can reject wrong-account sign-ins
  if (userId) {
    setClientCookie(REAUTH_EXPECTED_COOKIE, userId, {
      path: '/',
      maxAge: 300,
      sameSite: 'Lax',
    });
  }

  // Mark that a reconnection is in progress so we can handle the redirect back
  safeSessionWrite(REAUTH_STORAGE_KEYS.reconnect, '1');
  if (reconnectReturnPath != null) {
    safeSessionWrite(REAUTH_STORAGE_KEYS.reconnectReturn, reconnectReturnPath);
  } else {
    removeReauthStorageKey('reconnectReturn');
  }
  if (accountId != null && accountId !== '') {
    safeSessionWrite(REAUTH_STORAGE_KEYS.reconnectAccountId, accountId);
  } else {
    removeReauthStorageKey('reconnectAccountId');
  }

  if (isCrossEnvironmentAuth()) {
    const returnUrl = `${globalThis.location.origin}${withBasePath('/api/auth/cross-env-callback')}`;
    const loginUrl = new URL(`${AUTH_ORIGIN}/api/auth/cross-env-login`);
    loginUrl.searchParams.set('returnUrl', returnUrl);
    if (userId) loginUrl.searchParams.set('expectedUserId', userId);
    if (loginHint) loginUrl.searchParams.set('loginHint', loginHint);
    // Bare path (no basePath) — the local origin owns its own basePath; the
    // auth origin treats this as opaque path-only data and validates it before
    // signing it into the handoff JWT.
    loginUrl.searchParams.set('callbackUrl', callbackUrl);
    // Cross-env scope forwarding: the auth origin (not us) is the side that
    // calls signIn('google', { redirectTo, redirect: false }) and receives
    // the OAuth URL, so we cannot pass authorizationParams.scope here the
    // way the same-origin branch does. Instead we forward the precomputed
    // full scope string as a query param; cross-env-login/route.ts reads it
    // and overrides `scope` on the OAuth URL it produces.
    //
    // Why this matters: if this line is missing, Google sees only the base
    // scope from the provider config, no incremental-consent screen is
    // shown for the new scope, and the user goes through OAuth without
    // ever being prompted — a silent no-op that looks exactly like the
    // button is broken. See famfam/docs/architecture/oauth-scope-plumbing.md
    // for the full four-hop chain and the checklist for adding new scopes.
    const crossEnvScope = scopeParamFor(additionalScopes);
    if (crossEnvScope != null) {
      loginUrl.searchParams.set('scope', crossEnvScope);
    }
    globalThis.location.assign(loginUrl.toString());
    return;
  }

  // access_type and prompt come from the NextAuth provider defaults; signIn merges them.
  const authorizationParams: Record<string, string> = {
    include_granted_scopes: GOOGLE_OAUTH_AUTHORIZATION_PARAMS.include_granted_scopes,
  };
  if (loginHint) authorizationParams.login_hint = loginHint;
  const sameOriginScope = scopeParamFor(additionalScopes);
  if (sameOriginScope != null) {
    authorizationParams.scope = sameOriginScope;
  }
  await signIn('google', { callbackUrl: withBasePath(callbackUrl) }, authorizationParams);
}

/**
 * Start a fresh Google account link flow: adds a new account, distinct from
 * reauthing one already linked (see {@link startReauthFlow}).
 *
 * Sets `LINK_INTENT_COOKIE` before initiating the redirect so that, once
 * OAuth completes, the NextAuth callback knows this is an intentional
 * "link another account" action (versus a primary sign-in or a reauth).
 *
 * Also co-sets `LINK_INITIATOR_USER_ID` (the id of the currently signed-in
 * user who clicked "Add another account") so the signIn callback can recover
 * the initiator's user id without re-entering `auth()` — that would be a
 * layering inversion (calling `auth()` from inside the very callback that is
 * constructing a session). Both cookies share the same lifecycle and are
 * cleared by the signIn callback in lockstep.
 *
 * `userId` is required when the user is signed in (the normal case for a
 * link flow). Callers without a session should still pass `null`; the
 * initiator cookie is then omitted and the callback skips outcome detection.
 */
export async function startLinkFlow(options: {
  section: string;
  callbackUrl?: string;
  userId: string | null;
}): Promise<void> {
  const returnPath = options.callbackUrl ?? `/settings?section=${options.section}`;
  setClientCookie(LINK_INTENT_COOKIE, '1', { path: '/', maxAge: 300, sameSite: 'Lax' });
  if (options.userId != null && options.userId !== '') {
    setClientCookie(LINK_INITIATOR_USER_ID, options.userId, {
      path: '/',
      maxAge: 300,
      sameSite: 'Lax',
    });
  }
  await initiateGoogleAuthRedirect({
    callbackUrl: returnPath,
    reconnectReturnPath: returnPath,
  });
}

/**
 * Re-authenticate an existing linked Google account, taking a full
 * `LinkedAccount` as a convenience shape for callers that already hold one
 * (people-settings, the reauth banner). Callers that only know the
 * accountId + email — e.g. `EmailOptInCard`, which would otherwise
 * fabricate a placeholder `LinkedAccount` — should use the narrower
 * {@link startReauthFlowForAccount} entry point.
 *
 * Does not set LINK_INTENT_COOKIE — server uses loginHint + session identity
 * to distinguish reauth from a new account link. Do not add LINK_INTENT_COOKIE
 * here.
 */
export async function startReauthFlow(options: {
  account: LinkedAccount;
  section: string;
  userId?: string | null;
  userEmail?: string | null;
  callbackUrl?: string;
  /**
   * Extra OAuth scopes to request on top of the base list (e.g.
   * `['gmail.readonly']` for the email-intelligence opt-in flow).
   */
  additionalScopes?: readonly string[];
}): Promise<void> {
  return startReauthFlowForAccount({
    accountId: options.account.id,
    accountEmail: options.account.email,
    section: options.section,
    ...(options.userId !== undefined && { userId: options.userId }),
    ...(options.userEmail !== undefined && { userEmail: options.userEmail }),
    ...(options.callbackUrl !== undefined && { callbackUrl: options.callbackUrl }),
    ...(options.additionalScopes !== undefined && { additionalScopes: options.additionalScopes }),
  });
}

/**
 * Narrow reauth entry point — takes only the data the caller actually has
 * (an accountId + the email used to populate Google's `login_hint`) instead
 * of forcing every caller to construct a `LinkedAccount` shape with
 * `extraScopes` / `isPrimary` / `needsReauth` fields it doesn't know.
 *
 * Use this from any UI surface that knows it's reauthing one specific
 * account but doesn't otherwise hold a `LinkedAccount` (e.g. the Email
 * opt-in card, which has the account id from the email-account row but
 * not the broader account record).
 */
export async function startReauthFlowForAccount(options: {
  accountId: string;
  /** Forwarded as `login_hint` so Google selects the right account on the
   *  consent screen. Use `null` if unknown. */
  accountEmail?: string | null;
  section: string;
  userId?: string | null;
  userEmail?: string | null;
  callbackUrl?: string;
  /**
   * Extra OAuth scopes to request on top of the base list. The caller
   * passes only what it needs (e.g. `['gmail.readonly']`); the server
   * decides whether to honor each.
   */
  additionalScopes?: readonly string[];
}): Promise<void> {
  const returnPath = options.callbackUrl ?? `/settings?section=${options.section}`;
  await initiateGoogleAuthRedirect({
    callbackUrl: returnPath,
    reconnectReturnPath: returnPath,
    loginHint: options.accountEmail ?? null,
    userId: options.userId ?? null,
    accountId: options.accountId,
    ...(options.additionalScopes != null && { additionalScopes: options.additionalScopes }),
  });
}
